import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Languages } from '@s4l/i18n/types/Languages'
import { ALLOWED_MARKETS } from './Market'

export const LanguageValidation = z.object({
  value: z.enum(Languages, { required_error: '[Language] value is required' }),
})

const MARKET_TO_LANGUAGE = {
  ae: 'ar',
  au: 'en',
  at: 'de',
  br: 'pt',
  cz: 'cs',
  hu: 'hu',
  de: 'de',
  dk: 'da',
  ee: 'et',
  eg: 'ar',
  es: 'es',
  fi: 'fi',
  fr: 'fr',
  gr: 'el',
  hr: 'hr',
  ie: 'en',
  it: 'it',
  lt: 'lt',
  lv: 'lv',
  mx: 'es',
  nl: 'nl',
  no: 'nn',
  nz: 'en',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  sa: 'ar',
  se: 'sv',
  sk: 'sk',
  si: 'sl',
  tr: 'tr',
  uk: 'en',
  us: 'en',
  za: 'en',
  zz: 'en',
} as unknown as Record<(typeof ALLOWED_MARKETS)[number], Languages | undefined>

export class Language extends Model {
  static create({ value }: z.infer<typeof LanguageValidation>) {
    LanguageValidation.parse({ value })
    return new Language(value)
  }

  static createOrEmpty({ value }: z.infer<typeof LanguageValidation>) {
    try {
      return Language.create({ value })
    } catch {
      return Language.empty()
    }
  }

  static empty() {
    return new Language()
  }

  static default() {
    return Language.create({ value: 'en' })
  }

  static fromMarket(market?: (typeof ALLOWED_MARKETS)[number]) {
    if (!market) return Language.empty()
    const language = MARKET_TO_LANGUAGE[market]
    if (!language) return Language.empty()
    return Language.create({ value: language })
  }

  static fromJSON(json: ReturnType<Language['toJSON']>) {
    if (!json.value) {
      return Language.empty()
    }
    return Language.create({ value: json.value })
  }

  constructor(private readonly _value?: Languages) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  isEmpty() { return this._value === undefined } // prettier-ignore

  toJSON() {
    return { value: this.value ?? null }
  }
}
